import { css } from '@emotion/react';
import { PaymentMethod, PaymentMethodStatus } from '@frontend/api-payment-plans';
import { useTranslation } from '@frontend/i18n';
import { getCardBrand } from '@frontend/payments-card-brand';
import { PaymentMethodCardUI } from '@frontend/payments-card-on-file';
import { titleCase } from '@frontend/string';
import { theme } from '@frontend/theme';
import { Button, SkeletonLoader } from '@frontend/design-system';
import { useDeleteCardOnFileJustifi } from '../../../../../../hooks/justifi/use-delete-card-on-file-justifi';
import { usePaymentMethodsJustifi } from '../../../../../../hooks/justifi/use-payment-methods-justifi';

const containerStyles = css({
  display: 'flex',
  flexDirection: 'column',
  overflowY: 'auto',
  paddingRight: theme.spacing(2.25),
  marginBottom: theme.spacing(2),
  gap: theme.spacing(2),
  flex: 1,
  flexShrink: 0,
  maxHeight: 280,
});

export type PaymentMethodsListProps = {
  locationId?: string;
  personId?: string;
  paymentsUrl?: string | null;
  selectedPM?: PaymentMethod;
  onError?: (message: string) => void;
  onClickAddNewCard: () => void;
  onSelectPaymentMethod: (pm: PaymentMethod) => void;
  onDeletePaymentMethod?: (paymentMethod: PaymentMethod) => void;
};

export const PaymentMethodsList = ({
  personId,
  locationId,
  paymentsUrl,
  selectedPM,
  onError,
  onClickAddNewCard,
  onSelectPaymentMethod,
  onDeletePaymentMethod,
}: PaymentMethodsListProps) => {
  const { t } = useTranslation('payments');

  const { paymentMethods, isLoading: isLoadingPaymentMethods } = usePaymentMethodsJustifi({
    paymentsUrl,
    locationId,
    personId,
    selectedPM,
    onSelectPaymentMethod,
    onError: (message) => {
      onError?.(message || t('Error in retrieving saved cards.'));
    },
  });

  const { isDeleting, requestDelete, ConFirmDeleteModal } = useDeleteCardOnFileJustifi({
    paymentsUrl,
    locationId,
    personId,
    onSuccess: (paymentMethod) => {
      onDeletePaymentMethod?.(paymentMethod);
    },
    onError: (message) => {
      onError?.(message || t('Error deleting payment method.'));
    },
  });

  const isLoading = isLoadingPaymentMethods || isDeleting;

  if (isLoading) {
    return <PaymentMethodListLoader />;
  }

  return (
    <div>
      <div css={containerStyles}>
        {paymentMethods.map((paymentMethod) => (
          <PaymentMethodCardUI
            key={paymentMethod.id}
            brand={getCardBrand(paymentMethod.card?.brand)}
            title={titleCase(paymentMethod.card?.brand)}
            last4={paymentMethod.card?.last4}
            expMonth={paymentMethod.card?.expMonth}
            expYear={paymentMethod.card?.expYear}
            methodId={paymentMethod.id}
            selected={paymentMethod.id === selectedPM?.id}
            onClick={() => onSelectPaymentMethod(paymentMethod)}
            disabled={paymentMethod.status === PaymentMethodStatus.PAYMENT_METHOD_STATUS_EXPIRED}
            status={paymentMethod.status}
            actions={[
              {
                icon: 'trash',
                label: t('Delete'),
                onClick: () => requestDelete(paymentMethod),
              },
            ]}
          />
        ))}
      </div>
      <Button
        variant='secondary'
        iconName='plus-small'
        css={{ width: 'max-content' }}
        disabled={isLoading}
        onClick={onClickAddNewCard}
      >
        {t('Add New Card')}
      </Button>
      {ConFirmDeleteModal}
    </div>
  );
};

export const PaymentMethodListLoader = () => {
  return (
    <div css={containerStyles}>
      {[1, 2].map((val) => (
        <div css={{ width: '100%', height: 82, flexShrink: 0 }} key={val}>
          <SkeletonLoader />
        </div>
      ))}
    </div>
  );
};
